import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type ThemePreference = 'system' | 'light' | 'dark';

export const THEME_STORAGE_KEY = 'theme';

/**
 * Light/dark choice per device. "system" follows prefers-color-scheme; "light"/"dark" set
 * data-theme on <html>, which the SCSS theme honours over the system setting. The same
 * logic runs as an inline script in index.html before the app boots, so there is no flash.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly media = window.matchMedia?.('(prefers-color-scheme: dark)');
  private readonly preferenceSource = new BehaviorSubject<ThemePreference>(this.readStored());
  readonly preference$ = this.preferenceSource.asObservable();

  constructor() {
    this.apply(this.preferenceSource.value);
  }

  get preference(): ThemePreference {
    return this.preferenceSource.value;
  }

  /** what is actually shown right now */
  get isDark(): boolean {
    const pref = this.preferenceSource.value;
    return pref === 'dark' || (pref === 'system' && !!this.media?.matches);
  }

  setPreference(pref: ThemePreference): void {
    try {
      if (pref === 'system') localStorage.removeItem(THEME_STORAGE_KEY);
      else localStorage.setItem(THEME_STORAGE_KEY, pref);
    } catch {
      // storage blocked: still apply for this visit
    }
    this.apply(pref);
    this.preferenceSource.next(pref);
  }

  private readStored(): ThemePreference {
    try {
      const v = localStorage.getItem(THEME_STORAGE_KEY);
      return v === 'light' || v === 'dark' ? v : 'system';
    } catch {
      return 'system';
    }
  }

  private apply(pref: ThemePreference): void {
    const root = document.documentElement;
    if (pref === 'system') delete root.dataset['theme'];
    else root.dataset['theme'] = pref;
    // "only light" also keeps Chrome's auto dark mode off when light was picked on purpose
    const scheme = pref === 'system' ? 'light dark' : pref === 'light' ? 'only light' : 'dark';
    document.querySelector('meta[name="color-scheme"]')?.setAttribute('content', scheme);
  }
}
